/**
 * Service for calling the Settings API and denormalizing distributors within WebSked
 * @author Brendan Magee
 */

function distributorService($http, $translate) {
  const service = {};
  const settingsBase = '/settings-readonly/api/v1';

  const mapDistributor = (map, distributor) => {
    const { id, category, name } = distributor;
    if (!map.has(id)) {
      map.set(id, { category, name });
    }
    return map;
  };

  service.getDistributorMap = async () => {
    try {
      const response = await $http.get(`${settingsBase}/distributor`);
      const { rows } = response.data;
      return {
        distributors: rows.reduce(mapDistributor, new Map())
      };
    } catch (error) {
      return { error };
    }
  };

  const mapCategory = (categoryData, category) => {
    categoryData.list.push({
      id: category,
      name: $translate.instant(`DISTRIBUTOR.CATEGORY.${category.toUpperCase()}`)
    });
    categoryData.map.set(category, []);
    return categoryData;
  };

  service.getCategories = async () => {
    try {
      const response = await $http.get(`${settingsBase}/distributor/categories`);
      const { rows } = response.data;
      return {
        categories: rows.reduce(mapCategory, { map: new Map(), list: [] })
      };
    } catch (error) {
      return { error };
    }
  };

  /**
  * Caches and returns distributors and categories;
  * returns cached values when available
  * @param {string} type [type='distributors']
  * @returns {Promise} Promise of distributor or category data
  */
  service.getByType = async (type = 'distributors') => {
    if (type !== 'distributors' && type !== 'categories') {
      throw new Error('This method requires a "type" argument of "distributors" or "categories"');
    }

    if (service[type]) {
      return service[type];
    }

    const method = type === 'distributors'
      ? 'getDistributorMap'
      : 'getCategories';

    const { [type]: data, error } = await service[method]();

    if (data) {
      service[type] = data;
      return data;
    } else if (error) {
      service.error = error;
    }

    return null;
  };

  /**
  * Sorts distributors into arrays by category;
  * orders each array alphabetically by distributor name;
  * updates the categories cache
  * @returns {Promise} Promise of category data
  */
  service.getDistributorsByCategory = async () => {
    // Return the cached version if the map is already populated
    if (service.categories && service.categories.distributorsMapped) {
      return service.categories;
    }

    try {
      const [distributors, categories] = await Promise.all([
        service.getByType('distributors'),
        service.getByType('categories')
      ]);

      // Sort the distributors into appropriate categories
      distributors.forEach(({ category, name }, id) => {
        categories.map.get(category).push({ id, name, category });
      });

      // Sort each each array of distributors alphabetically by `name`
      categories.map.forEach((distributorArray) => {
        distributorArray.sort((first, second) => first.name.localeCompare(second.name));
      });

      // Update the categories cache
      categories.distributorsMapped = true;
      service.categories = categories;

      return categories;
    } catch (error) {
      return service.categories || null;
    }
  };

  return service;
}

const serviceArray = [
  '$http',
  '$translate',
  distributorService
];

module.exports = (ngModule) => {
  ngModule.factory('distributorService', serviceArray);
};
